<template>
    <form :id="id" @submit.prevent="handleSubmit" class="form-horizontal">
        <fieldset :disabled="disabled">
            <x-form-group
                label="Kategori"
                :error="getError('category_id')"
                :is-required="false"
            >
                <x-ajax-dropdown
                    url="/api/group-categories"
                    label="label"
                    :allow-empty="true"
                    v-model="fields.category"
                    class="form-control"
                />
            </x-form-group>

            <x-form-group
                label="Label"
                :error="getError('label')"
                :is-required="true"
            >
                <input
                    type="text"
                    class="form-control"
                    v-model="fields.label"
                    required
                />
            </x-form-group>

            <x-form-group
                label="Available Roles"
                :error="getError('role_ids')"
                :is-required="false"
            >
                <x-ajax-dropdown
                    url="/api/group-roles"
                    label="label"
                    :allow-empty="true"
                    v-model="fields.roles"
                    class="form-control"
                    :multiple="true"
                />
            </x-form-group>

            <x-form-group
                label="Deskripsi"
                :error="getError('description')"
                :is-required="false"
            >
                <textarea class="form-control" v-model="fields.description" />
            </x-form-group>

            <x-form-group
                label="Pencatatan Journey"
                :error="getError('record_journey')"
            >
                <x-checkbox-wrapper label="Aktifkan pencatatan journey anggota">
                    <input type="checkbox" v-model="fields.record_journey" />
                </x-checkbox-wrapper>
            </x-form-group>

            <x-form-group
                :label="`Anggota (${fields.members.length})`"
                :error="getError('members')"
                :is-required="false"
            >
                <x-table
                    :headers="[
                        '',
                        'Jemaat',
                        'Role',
                        'Status',
                        'Tanggal Bergabung',
                    ]"
                >
                    <tr v-for="(member, index) in fields.members" :key="index">
                        <td>
                            <x-delete-item-button
                                @click="deleteMember(index)"
                            />
                        </td>
                        <td>
                            <x-form-group
                                :error="getError(`members.${index}.member_id`)"
                            >
                                <x-ajax-dropdown
                                    url="/api/members"
                                    :blacklist="memberIds"
                                    label="name"
                                    :value="member.member"
                                    @input="(value) => setMember(index, value)"
                                    class="form-control"
                                />
                                <x-checkbox-wrapper
                                    label="Terima SMS Ultah"
                                    v-if="shouldDisplaySMSFlag"
                                >
                                    <input
                                        type="checkbox"
                                        v-model="member.birthday_sms"
                                    />
                                </x-checkbox-wrapper>
                                <x-checkbox-wrapper
                                    label="Tampilkan sebagai leader di laporan"
                                >
                                    <input
                                        type="checkbox"
                                        v-model="member.is_leader"
                                    />
                                </x-checkbox-wrapper>
                                <x-checkbox-wrapper label="Include di laporan">
                                    <input
                                        type="checkbox"
                                        v-model="member.include_in_report"
                                    />
                                </x-checkbox-wrapper>
                            </x-form-group>
                        </td>
                        <td>
                            <x-form-group
                                :error="getError(`members.${index}.role_id`)"
                            >
                                <select
                                    class="form-control"
                                    v-model="member.role_id"
                                    style="min-width: 130px"
                                >
                                    <option
                                        :value="null"
                                        :selected="!Boolean(member.role_id)"
                                    >
                                        Anggota
                                    </option>
                                    <option
                                        v-for="(role, index) in fields.roles"
                                        :key="index"
                                        :value="role.id"
                                    >
                                        {{ role.label }}
                                    </option>
                                </select>
                            </x-form-group>
                        </td>
                        <td>
                            <x-checkbox-wrapper label="Aktif">
                                <input
                                    type="checkbox"
                                    value="1"
                                    v-model="member.is_active"
                                />
                            </x-checkbox-wrapper>
                        </td>
                        <td>
                            <x-form-group
                                :error="getError(`members.${index}.join_date`)"
                            >
                                <input
                                    type="date"
                                    class="form-control"
                                    :value="member.join_date"
                                    @input="
                                        (e) =>
                                            setJoinDate(index, e.target.value)
                                    "
                                />
                            </x-form-group>
                        </td>
                    </tr>
                    <template #footer="{ colspan }">
                        <tr>
                            <td :colspan="colspan" class="text-center">
                                <x-add-item-button @click="addMember" />
                            </td>
                        </tr>
                    </template>
                </x-table>
            </x-form-group>

            <x-form-group
                label="Trigger Masuk"
                :error="getError(`enter_trigger`)"
            >
                <x-input-group-trigger
                    :errorPrefix="`enter_trigger.`"
                    :errors="errors"
                    :initialFields="fields.enter_trigger"
                    @input="(trigger) => (fields.enter_trigger = trigger)"
                />
            </x-form-group>

            <x-form-group
                label="Trigger Keluar"
                :error="getError(`exit_trigger`)"
            >
                <x-input-group-trigger
                    :errorPrefix="`exit_trigger.`"
                    :errors="errors"
                    :initialFields="fields.exit_trigger"
                    @input="(trigger) => (fields.exit_trigger = trigger)"
                />
            </x-form-group>
        </fieldset>
    </form>
</template>

<script>
import XAddItemButton from "../../components/XAddItemButton.vue";
import XAjaxDropdown from "../../components/XAjaxDropdown.vue";
import XCheckboxWrapper from "../../components/XCheckboxWrapper.vue";
import XDeleteItemButton from "../../components/XDeleteItemButton.vue";
import XFilterBox from "../../components/XFilterBox.vue";
import XFormGroup from "../../components/XFormGroup.vue";
import XInputGroupTrigger from "../../components/XInputGroupTrigger.vue";
import XTable from "../../components/XTable.vue";
import { dateFormat } from "../../constants";
import FormErrorMixin from "../../mixins/FormErrorMixin";
import PropertySyncMixin from "../../mixins/PropertySyncMixin";

const defaultMember = {
    member: null,
    role_id: null,
    join_date: moment().format(dateFormat),
    is_leader: 0,
    include_in_report: 1,
    birthday_sms: 1,
    is_active: true,
};

export default {
    components: {
        XFormGroup,
        XFilterBox,
        XCheckboxWrapper,
        XTable,
        XAddItemButton,
        XAjaxDropdown,
        XDeleteItemButton,
        XInputGroupTrigger,
    },
    computed: {
        memberIds() {
            return this.fields.members
                .map((member) => member?.member?.id)
                .filter((id) => Boolean(id));
        },
        roleIds() {
            return this.fields.roles.map((role) => role.id);
        },
        shouldDisplaySMSFlag() {
            return (
                _.get(this.fields, "category.sms_birthday_blast", false) == true
            );
        },
    },
    created() {
        this.fields = {
            ...this.fields,
            ...this.initialFields,
        };
    },
    data() {
        return {
            fields: {
                category_id: null,
                category: null,
                description: null,
                label: null,
                members: [],
                record_journey: true,
                roles: [],
                enter_trigger: { active: false },
                exit_trigger: { active: false },
            },
        };
    },
    methods: {
        addMember() {
            this.fields.members.push({ ...defaultMember });
        },
        deleteMember(target) {
            this.fields.members = this.fields.members.filter(
                (member, index) => index != target
            );
        },
        handleSubmit() {
            const { roles, ...fields } = this.fields;
            this.$emit("submit", {
                ...fields,
                members: fields.members.map((member) => ({
                    member_id: member.member?.id || null,
                    role_id: member.role_id,
                    join_date: member.join_date,
                    is_leader: member.is_leader,
                    include_in_report: member.include_in_report,
                    birthday_sms: member.birthday_sms,
                    is_active: member.is_active,
                })),
                role_ids: this.roleIds,
            });
        },
        setMember(index, value) {
            this.$set(this.fields.members[index], "member", value);
        },
        setJoinDate(index, value) {
            this.$set(this.fields.members[index], "join_date", value);
        },
        setRole(index, value) {
            this.$set(this.fields.members[index], "role", value);
        },
    },
    mixins: [FormErrorMixin, PropertySyncMixin],
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        errors: {
            type: Object,
            required: false,
            default: null,
        },
        id: {
            type: String,
            required: false,
            default: null,
        },
        initialFields: {
            type: Object,
            required: false,
            default: () => ({}),
        },
    },
    watch: {
        "fields.category": {
            handler(relation) {
                this.syncToProp(relation?.id || null, "fields.category_id");
            },
            deep: true,
            immediate: true,
        },
        "fields.roles": {
            handler(roles, previousRoles) {
                if (typeof previousRoles === "undefined") {
                    return;
                }

                const deletedRoles = previousRoles.filter((role) => {
                    return !this.roleIds.includes(role.id);
                });

                const deletedRoleIds = deletedRoles.map((role) => role.id);

                this.fields.members.forEach((member, index) => {
                    if (!deletedRoleIds.includes(member.role_id)) {
                        return;
                    }

                    this.$set(this.fields.members, index, {
                        ...member,
                        role_id: null,
                    });
                });
            },
            deep: true,
        },
    },
};
</script>
